import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// RM Rejection Register DTO
export interface RmRejectionRegisterDto {
  id?: number;
  endgoodId?: number;
  processId?: number;
  rejectQty: number;
  rejectDate: string; // LocalDate format: YYYY-MM-DD
  branchId?: number;
  rawItemId: number;
  heatNo?: string;
  type?: string;
  reason?: string;
  grnNumber?: string;
  createUser?: number;
  updateUser?: number;
  createDatetime?: string;
  updateDatetime?: string;
}

// RM Rejection Register API Service
class RmRejectionApiService extends BaseApiService {
  
  // Create a new rejection record
  async createRejection(
    rejectionData: RmRejectionRegisterDto,
    createdBy?: number
  ): Promise<ApiResponse<RmRejectionRegisterDto>> {
    try {
      const params = new URLSearchParams();
      if (createdBy) {
        params.append('createdBy', createdBy.toString());
      }
      
      const queryString = params.toString();
      const url = `${API_ENDPOINTS.RM_REJECTION_REGISTER.CREATE}${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.makeRequest<ApiResponse<RmRejectionRegisterDto>>(
        url,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(rejectionData),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.success) {
        toast.success('Rejection record created successfully');
        return response;
      } else {
        throw new Error(response.message || 'Failed to create rejection record');
      }
    } catch (error) {
      // console.error('RmRejectionApiService: Error creating rejection record:', error);
      toast.error('Failed to create rejection record');
      throw error;
    }
  }

  // Get all rejection records
  async getAllRejections(): Promise<ApiResponse<RmRejectionRegisterDto[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<RmRejectionRegisterDto[]>>(
        API_ENDPOINTS.RM_REJECTION_REGISTER.LIST,
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('RmRejectionApiService: Error fetching rejection records:', error);
      toast.error('Failed to fetch rejection records');
      throw error;
    }
  }

  // Get rejection by ID
  async getRejectionById(id: number): Promise<ApiResponse<RmRejectionRegisterDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<RmRejectionRegisterDto>>(
        API_ENDPOINTS.RM_REJECTION_REGISTER.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('RmRejectionApiService: Error fetching rejection record:', error);
      toast.error('Failed to fetch rejection record');
      throw error;
    }
  }

  // Update rejection record
  async updateRejection(
    id: number,
    rejectionData: RmRejectionRegisterDto,
    updatedBy?: number
  ): Promise<ApiResponse<RmRejectionRegisterDto>> {
    try {
      const params = new URLSearchParams();
      if (updatedBy) {
        params.append('updatedBy', updatedBy.toString());
      }
      
      const queryString = params.toString();
      const url = `${API_ENDPOINTS.RM_REJECTION_REGISTER.UPDATE(id)}${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.makeRequest<ApiResponse<RmRejectionRegisterDto>>(
        url,
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(rejectionData),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.success) {
        toast.success('Rejection record updated successfully');
        return response;
      } else {
        throw new Error(response.message || 'Failed to update rejection record');
      }
    } catch (error) {
      // console.error('RmRejectionApiService: Error updating rejection record:', error);
      toast.error('Failed to update rejection record');
      throw error;
    }
  }

  // Delete rejection record
  async deleteRejection(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.RM_REJECTION_REGISTER.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Rejection record deleted successfully');
        return response;
      } else {
        throw new Error(response.message || 'Failed to delete rejection record');
      }
    } catch (error) {
      // console.error('RmRejectionApiService: Error deleting rejection record:', error);
      toast.error('Failed to delete rejection record');
      throw error;
    }
  }
}

export const rmRejectionApiService = new RmRejectionApiService();


